import {observable, computed, action, runInAction} from 'mobx';
import {countryCodes} from '@techsee/techsee-country-codes/lib/index';
import filter from 'lodash/filter';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import every from 'lodash/every';
import startsWith from 'lodash/startsWith';
import find from 'lodash/find';
import get from 'lodash/get'; // Add this import
import {ITranslate} from '../../services/LocalizationService';

export interface ICountryOption {
    display: string;
    dial: string;
    flag: string;
    code: string;
    adminSelect?: boolean;
}

export interface ICountrySelectController {
    readonly filteredCountries: ICountryOption[];
    readonly moreCountryCodes: string;
    readonly useNewInterface: boolean;

    onCountrySelect(item: ICountryOption): void;
    selectedCountryCodeClicked(): void;
    getCurrentCountryCode(): string;
    validateCountryCode(countryCode: string): boolean;
    init(): void;

    addCountryCodeToSms: boolean;
    countryCodesNoMatch: string;
    countryCodeText: string;
    sms: boolean;
    countryCodesPlaceHolder: string;
}

export class CountrySelectController implements ICountrySelectController {
    @observable private allCountryCodes: ICountryOption[] = [];
    @observable private moreCountries: boolean = false;
    @observable private loaded: boolean = false;
    @observable private keepCountryCodeOnNextUpdate: boolean = true;
    @observable private initialCountryCodesGet: boolean = false;
    @observable private lastModel: string = '';
    @observable private countryCodeLoading: boolean = false;
    @observable public countryCodesPlaceHolder: string = '';
    @observable public countryCodesNoMatch: string = '';

    private translate: ITranslate;
    @observable private _useNewInterface: boolean;
    public getFromLocalStorage: (key: string) => any;
    public onCountryCodeChange?: (countryCode: string) => void;
    public addCountryCodeToSms: boolean;
    @observable private accountCountryCode: string;
    @observable private selectedCountries: any[];
    public sms: boolean;
    constructor(
        translate: ITranslate,
        getFromLocalStorage: (key: string) => any,
        useNewInterface: boolean,
        addCountryCodeToSms: boolean,
        accountCountryCode: string,
        selectedCountries: any[],
        sms: boolean,
        countryCodeText: string,
        onCountryCodeChange?: (countryCode: string) => void
    ) {
        this.translate = translate;
        this.getFromLocalStorage = getFromLocalStorage;
        this._useNewInterface = useNewInterface;
        this.addCountryCodeToSms = addCountryCodeToSms;
        this.accountCountryCode = accountCountryCode;
        this.selectedCountries = selectedCountries;
        this.sms = sms;
        this.onCountryCodeChange = onCountryCodeChange;
        this.countryCodeText = countryCodeText ?? '';
    }

    public countryCodeText: string;

    @computed
    get filteredCountries(): ICountryOption[] {
        if (!this.loaded) {
            this.updateCountries(countryCodes);
            this.loaded = true;
        }

        // Convert string country codes to country objects first
        const selectedCountryObjects = this.selectedCountries
            .map((countryCode) => {
                if (typeof countryCode === 'string') {
                    return countryCodes.find((country) => country.code === countryCode);
                }
                return countryCode;
            })
            .filter((country) => country !== undefined);

        // Get base codes - if not moreCountries, return selectedCountries, otherwise return all countryCodes
        let codes = this.moreCountries ? countryCodes : selectedCountryObjects;

        // Handle moreCountries logic
        if (this.moreCountries) {
            // Filter out the "more countries" option from selectedCountries
            const filteredSelectedCountries = filter(
                selectedCountryObjects,
                (country) => country.display !== this.moreCountryCodes
            );

            // Get countries that are not in selectedCountries
            const notSelectedCountries = filter(codes, (country) =>
                every(filteredSelectedCountries, (selectedCountry) => country.dial !== selectedCountry.dial)
            );

            // Mark selected countries as admin select
            map(filteredSelectedCountries, (country) => {
                country.adminSelect = true;
            });

            // Combine selected and not selected countries
            codes = filteredSelectedCountries.concat(notSelectedCountries);
        }

        return codes;
    }

    @action
    private selectFirstCountry = (firstCountry: ICountryOption): void => {
        this.lastModel = firstCountry.dial;
        if (this.onCountryCodeChange) {
            this.onCountryCodeChange(firstCountry.dial);
        }
    };

    get moreCountryCodes(): string {
        return this.translate('INVITE.VIEW.MORE_COUNTRY_CODES');
    }

    get useNewInterface(): boolean {
        return this._useNewInterface;
    }

    private updateCountries(codes: any[]): void {
        this.allCountryCodes = map(codes, (country) => {
            country.adminSelect = false;
            return country;
        });

        const defaultFromAdmin = `+${this.accountCountryCode}`;
        const isDefaultCode = (country: ICountryOption) => country.dial === defaultFromAdmin;

        const availableCountries = map(this.selectedCountries, (country) => this.getCountryByCode(country)).filter(
            (country): country is ICountryOption => country !== undefined
        );
        let defaultCountryCode = filter(availableCountries, (country) => isDefaultCode(country));

        if (isEmpty(defaultCountryCode)) {
            defaultCountryCode = this.getAllDefaultCountries(defaultFromAdmin);
        }

        const model = defaultCountryCode[0]?.dial || '';
        this.lastModel = model;
        if (this.onCountryCodeChange) {
            this.onCountryCodeChange(model);
        }
    }

    private getCountryByCode(countryCode: string): ICountryOption | undefined {
        return find(this.allCountryCodes, (country) => country.code === countryCode);
    }

    private getAllDefaultCountries(defaultCountry: string): ICountryOption[] {
        return filter(this.allCountryCodes, (country) => country.dial === defaultCountry);
    }

    @action
    onCountrySelect = (item: ICountryOption): void => {
        if (item && item.display === this.moreCountryCodes) {
            this.moreCountries = true;
            this.selectedCountryCodeClicked();
        } else if (item) {
            const countryCode = item.dial;
            this.lastModel = countryCode;
            if (this.onCountryCodeChange) {
                this.onCountryCodeChange(countryCode);
            }
        }
    };

    @action
    selectedCountryCodeClicked = (): void => {
        if (this.sms) {
            return;
        }

        if (this.onCountryCodeChange) {
            this.onCountryCodeChange('');
        }
    };

    getCurrentCountryCode(): string {
        if (startsWith(this.lastModel, '+')) {
            return this.lastModel.substring(1);
        }
        return this.lastModel;
    }

    validateCountryCode(countryCode: string): boolean {
        if (!countryCode) {
            return false;
        }

        if (!this.getFromLocalStorage('countryCodes')) {
            return true;
        }

        const dial = `+${countryCode}`;
        return !!find(this.getFromLocalStorage('countryCodes'), (code) => code.dial === dial);
    }

    @action
    init(): void {
        if (this.addCountryCodeToSms) {
            this.countryCodeLoading = false;
            this.countryCodesPlaceHolder = this.translate('INVITE.VIEW.COUNTRY_CODES_PLACEHOLDER');
            this.countryCodesNoMatch = this.translate('INVITE.VIEW.COUNTRY_CODES_NO_MATCH');
        }

        // Set default country code if none is provided
        if (!this.lastModel && countryCodes.length > 0) {
            const firstCountry = countryCodes[0];
            this.lastModel = firstCountry.dial;
            if (this.onCountryCodeChange) {
                this.onCountryCodeChange(firstCountry.dial);
            }
        }

        // Existing init logic
        if (this.addCountryCodeToSms) {
            setTimeout(() => {
                const countryCode = this.accountCountryCode && `+${this.accountCountryCode}`;
                if (!this.lastModel) {
                    this.lastModel = countryCode;
                    if (this.onCountryCodeChange) {
                        this.onCountryCodeChange(countryCode);
                    }
                }
            }, 0);
        }
    }

    @action
    initWithData(data: any): void {
        this.selectedCountries.push({display: this.moreCountryCodes, dial: '', flag: '', code: ''});

        if (data.lastUsedTOSCountryCode) {
            this.lastModel = data.lastUsedTOSCountryCode;
            if (this.onCountryCodeChange) {
                this.onCountryCodeChange(data.lastUsedTOSCountryCode);
            }
        } else if (data.preloadCountryCode) {
            this.lastModel = data.preloadCountryCode;
            if (this.onCountryCodeChange) {
                this.onCountryCodeChange(data.preloadCountryCode);
            }
        }

        // Handle account data if provided
        if (data.accountData) {
            this.selectedCountries = get(data.accountData, 'protectedSettings.selectedCountries');
            this.accountCountryCode = get(data.accountData, 'settings.countryCode');
        }
    }
}
